import SessionsIcon from "../../images/reports/sessionsIcon.svg?react";
import AssessmentsIcon from "../../images/reports/assessmentsIcon.svg?react";
import StudentsIcon from "../../images/reports/studentsIcon.svg?react";
import LiveIcon from "../../images/reports/liveIcon.svg?react";
import LiveHoursIcon from "../../images/reports/liveHoursIcon.svg?react";
import HoursIcon from "../../images/reports/hoursIcon.svg?react";
import SearchIcon from "../../images/reports/searchChartIcon.svg?react";
import NoChangeIcon from "../../images/reports/noChangeIcon.svg?react";
import IncreaseIcon from "../../images/reports/increaseIcon.svg?react";
import DecreaseIcon from "../../images/reports/decreaseIcon.svg?react";
import AverageHours from "../../images/reports/averageHours.svg?react";
import AverageSessions from "../../images/reports/averageSessions.svg?react";
import Flag from "../../images/reports/flaggedEventsIcon.svg?react";
import PlatformIcon from "../../images/reports/platformIcon.svg?react";
import AssessmentOverviewIcon from "../../images/reports/assessmentOverviewIcon.svg?react";
import { Circle } from "@mui/icons-material";
import { Box, Skeleton, Typography } from "@mui/material";
import {
	CoreCard,
	CoreCheckbox,
	CoreDataView,
	CoreDataViewColumn,
	CoreTabs
} from "@verity/components/core";
import { StatisticCard } from "@verity/components";
import { Chart, ChartEvent, ActiveElement } from "chart.js";
import { KeyboardEvent, useRef, useState, useEffect, useId, useMemo } from "react";
import i18n from "../../i18n";
import { badgeStyle, NoResults } from "./report-components";
import { StatsCardsData } from "./report-types";
import styles from "../reports.module.css";

type SessionData = {
	label: string;
	data?: number[];
	show: boolean;
	setShow: (value: boolean) => void;
	color: string;
};

type ReportTableRow = {
	id: string;
	method: string;
	[key: string]: string | number;
};

export const SummaryCard = (props: {
	title: string;
	number?: number;
	color: string;
	delta?: number;
	message: string;
	headerIcon?: React.ReactNode;
}) => {
	return (
		<CoreCard
			boxShadow
			style={{
				background: `radial-gradient(circle at 0% 0%, ${props.color}, white 20%)`,
			}}
		>
			<Box display="flex" flexDirection="column" height="100%">
				<div>
					<div style={{ marginBottom: "6px" }}>{props.headerIcon}</div>
					<div style={{ display: "flex", flexDirection: "row", gap: "6px" }}>
						<div style={{ display: "flex", flexDirection: "column", gap: "6px", marginRight: "auto" }}>
							<div className={styles.sectionCardTitle}>{props.title}</div>
							{props.number === undefined ? (
								<Skeleton width={"60px"}></Skeleton>
							) : (
								<div className={styles.sectionCardNumber}>{props.number}</div>
							)}
						</div>
						{props.delta !== undefined &&
							(props.delta > 0 ? <IncreaseIcon /> : props.delta < 0 ? <DecreaseIcon /> : <NoChangeIcon />)}
					</div>
				</div>
				<hr
					style={{
						border: "1.5px solid #EFF0F0",
						width: "100%",
						margin: "0px",
						marginTop: "6px",
						marginBottom: "7px",
					}}
				/>
				<div
					style={{
						display: "flex",
						flexDirection: "row",
						gap: "10px",
						marginTop: "auto",
						marginBottom: "1px",
					}}
				>
					{props.delta !== undefined ? (
						<div
							className={[
								styles.changeNumber,
								props.delta > 0
									? styles.increaseNumber
									: props.delta < 0
										? styles.decreaseNumber
										: styles.noChangeNumber,
							].join(" ")}
						>
							{props.message}
						</div>
					) : (
						<Skeleton width="150px" />
					)}
				</div>
			</Box>
		</CoreCard>
	);
};

export const SummaryValueCard = (props: {
	title: string;
	number?: number;
	text?: string;
	color: string;
	headerIcon?: React.ReactNode;
}) => {
	return (
		<CoreCard
			boxShadow
			style={{
				background: `radial-gradient(circle at 0% 0%, ${props.color}, white 20%)`,
				minHeight: "93px",
			}}
		>
			<Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
				<div>
					<div style={{ marginBottom: "6px" }}>{props.headerIcon}</div>
					<div style={{ display: "flex", flexDirection: "row" }}>
						<div style={{ display: "flex", flexDirection: "column", gap: "6px", marginRight: "auto" }}>
							<div className={styles.sectionCardTitle}>{props.title}</div>
							{props.number === undefined && props.text === undefined ? (
								<Skeleton width={"60px"}></Skeleton>
							) : (
								<div className={styles.sectionCardNumber}>
									{props.number !== undefined ? props.number : props.text !== undefined && props.text}
								</div>
							)}
						</div>
					</div>
				</div>
			</Box>
		</CoreCard>
	);
};

export function StatisticsCards({
	assessments,
	students,
	veritySessions,
	liveSessions,
	proctoringHours,
	liveHours,
	averageSessions,
	averageHours,
	endDate,
	liveEnabled,
}: StatsCardsData & { endDate: Date; liveEnabled?: boolean }) {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: {
					s: "repeat(1, 1fr)", // For extra small screens (1 column)
					md: "repeat(2, 1fr)", // For small screens (2 columns)
					lg: liveEnabled !== false ? "repeat(4, 1fr)" : "repeat(3, 1fr)", // For medium and larger screens (4 columns)
				},
				gridTemplateRows: "auto", // You can adjust rows similarly if needed
				columnGap: "30px",
				rowGap: "15px",
			}}
		>
			<StatisticCard
				header={i18n.t("instructor.components.reports.summaryCards.totalAssessmentsProctored")}
				message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
				icon={<AssessmentsIcon />}
				value={assessments?.total}
				delta={assessments?.month}
				dataLoading={assessments?.total === undefined || assessments?.month === undefined}
			/>
			{liveEnabled !== false && (
				<StatisticCard
					header={i18n.t("instructor.components.reports.summaryCards.totalLiveAttempts")}
					message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
					icon={<LiveIcon />}
					value={liveSessions?.total}
					delta={liveSessions?.month}
					dataLoading={liveSessions?.total === undefined || liveSessions?.month === undefined}
				/>
			)}
			<StatisticCard
				header={i18n.t("instructor.components.reports.summaryCards.totalAttempts")}
				message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
				icon={<SessionsIcon />}
				value={veritySessions?.total}
				delta={veritySessions?.month}
				dataLoading={veritySessions?.total === undefined || veritySessions?.month === undefined}
			/>
			<StatisticCard
				header={i18n.t("instructor.components.reports.summaryCards.avgAttemptsPerMonth")}
				message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
				icon={<AverageSessions />}
				value={averageSessions?.total && Math.round(averageSessions.total)}
				delta={averageSessions?.change && Math.round(averageSessions.change)}
				dataLoading={averageSessions?.total === undefined || averageSessions?.change === undefined}
			/>
			{liveEnabled !== false && (
				<StatisticCard
					header={i18n.t("instructor.components.reports.summaryCards.totalLiveHoursProctored")}
					message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
					icon={<LiveHoursIcon />}
					value={liveHours?.total && Math.round(liveHours.total * 10) / 10}
					delta={liveHours?.month && Math.round(liveHours.month * 10) / 10}
					dataLoading={liveHours?.total === undefined || liveHours?.month === undefined}
				/>
			)}
			<StatisticCard
				header={i18n.t("instructor.components.reports.summaryCards.totalUsersProctored")}
				message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
				icon={<StudentsIcon />}
				value={students?.total}
				delta={students?.month}
				dataLoading={students?.total === undefined || students?.month === undefined}
			/>
			<StatisticCard
				header={i18n.t("instructor.components.reports.summaryCards.totalHoursProctored")}
				message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
				icon={<HoursIcon />}
				value={proctoringHours?.total && Math.round(proctoringHours.total * 10) / 10}
				delta={proctoringHours?.month && Math.round(proctoringHours.month * 10) / 10}
				dataLoading={proctoringHours?.total === undefined || proctoringHours?.month === undefined}
			/>
			<StatisticCard
				header={i18n.t("instructor.components.reports.summaryCards.avgProctorHoursPerMonth")}
				message={endDate.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
				icon={<AverageHours />}
				value={averageHours?.total && Math.round(averageHours.total * 10) / 10}
				delta={averageHours?.change && Math.round(averageHours.change * 10) / 10}
				dataLoading={averageHours?.total === undefined || averageHours?.change === undefined}
			/>
		</Box>
	);
}

/**
 * Screen reader text for the graph. This is so that users who uses screen readers can hear the data
 */
function ScreenReaderTableResults({
	monthLabels,
	sessionData,
	id,
}: {
	monthLabels: string[][];
	sessionData: SessionData[];
	id: string;
}) {
	// Only show sessions that are visible
	const visibleSessions = sessionData.filter((s) => s.show);
	const visibleLabels = visibleSessions.map((s) => s.label).join(", ");
	const methodLabel =
		visibleLabels.length === 0
			? i18n.t("instructor.components.reports.canvas.noMethod")
			: i18n.t("instructor.components.reports.canvas.srCanvasSubtitle", {
					methods: visibleLabels,
				});

	return (
		<div className={styles["sr-only"]} id={id}>
			<span>
				{i18n.t("instructor.components.reports.canvas.srCanvasTitle")}
				{methodLabel}
			</span>
			{/* Monthly breakdown */}
			{visibleSessions.length > 0 &&
				monthLabels.map((labelParts, i) => {
					const month = labelParts.join(" ");
					const isAllZero = (sessionData[0]?.data?.[i] ?? 0) === 0; // If "All Methods" is 0, skip narratoring the rest of the methods

					const breakdown = isAllZero
						? i18n.t("instructor.components.reports.canvas.zeroAttempts")
						: visibleSessions
								.map(({ label, data }) => {
									const val = data?.[i] ?? 0;
									return i18n.t("instructor.components.reports.canvas.attemptsForLabel", {
										count: val,
										label: label.toLowerCase(),
									});
								})
								.join(". ");

					return (
						<span key={i}>
							{i18n.t("instructor.components.reports.canvas.monthSummary", {
								month,
								breakdown,
							})}
						</span>
					);
				})}
		</div>
	);
}

function ReportGraph({
	sessionData,
	monthLabels,
	loading,
}: {
	sessionData: SessionData[];
	monthLabels: string[][];
	loading?: boolean;
}) {
	const chartRef = useRef<HTMLCanvasElement>(null);
	const [chart, setChart] = useState<Chart | null>(null);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const canvasId = useId();

	const isAnyDatasetVisible = sessionData.some(session => session.show);

	const handleHover = (_event: ChartEvent, elements: ActiveElement[]) => {
		if (elements.length > 0) {
			const index = elements[0].index;
			setHoveredIndex(index);
		} else {
			setHoveredIndex(null);
		}
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (!chart || !isAnyDatasetVisible) return;

		const pointsCount =  Array.isArray(chart.data.labels) && chart.data.labels.length > 0
			? chart.data.labels.length
			: monthLabels.length;
		if (pointsCount === 0) return;

		let newIndex = hoveredIndex;
		if (event.key === "ArrowRight") {
			newIndex = hoveredIndex === null ? 0 : Math.min(hoveredIndex + 1, pointsCount - 1);
			event.preventDefault();
		} else if (event.key === "ArrowLeft") {
			newIndex = hoveredIndex === null ? pointsCount - 1 : Math.max(hoveredIndex - 1, 0);
			event.preventDefault();
		} else {
			return;
		}

		setHoveredIndex(newIndex);
	};

	useEffect(() => {
		if (!isAnyDatasetVisible && hoveredIndex !== null) setHoveredIndex(null);
	}, [isAnyDatasetVisible, hoveredIndex]);

	useEffect(() => {
		if (chart?.options?.scales?.x?.grid) {
			chart.options.animation = false;
			chart.options.scales.x.grid.color = (context) => {
				if (context.index === hoveredIndex) {
					return "rgba(100, 100, 100, 1)";
				} else {
					return "rgba(196, 196, 196, 1)";
				}
			};

			const labels = chart.data.labels ?? [];
			const validHoverIndex =
				hoveredIndex !== null &&
				hoveredIndex >= 0 &&
				(typeof labels.length !== "number" || hoveredIndex < labels.length);
			
			if (validHoverIndex) {
				const activeElements = chart.data.datasets.reduce<{ datasetIndex: number; index: number }[]>((acc, dataset, datasetIndex) => {
					const meta = chart.getDatasetMeta(datasetIndex);
					const element = meta?.data?.[hoveredIndex];
					const isDatasetHidden =
						meta?.hidden === true ||
						(dataset && typeof dataset.hidden === "boolean" && dataset.hidden);

					if (!isDatasetHidden && element) {
						acc.push({
							datasetIndex,
							index: hoveredIndex
						});
					}

					return acc;
				}, []);

				chart.setActiveElements(activeElements ?? []);
				if (chart.tooltip) {
					chart.tooltip.setActiveElements(activeElements ?? [], { x: 0, y: 0 });
				}
			}

			chart.update();
		}
	}, [hoveredIndex, chart]);

	useEffect(() => {
		return () => {
			if (chart) chart.destroy();
		}
	}, [chart]);

	useEffect(() => {
		if (loading) {
			if (chart) chart.destroy();
			setChart(null);
			return;
		}

		if (chartRef.current && !chart) {
			const ctx = chartRef.current.getContext("2d");
			if (ctx) {
				const newChart = new Chart(ctx, {
					type: "line",
					data: {
						labels: [],
						datasets: [],
					},
					options: {
						interaction: {
							mode: "index", // Highlight all datasets at the same index
							intersect: false, // Do not restrict to only the hovered point
						},
						onHover: handleHover,
						scales: {
							y: {
								beginAtZero: true,
								title: {
									display: true,
									text: i18n.t("instructor.components.reports.attempts"),
									color: "#000000",
									font: {
										weight: 400,
										size: 15,
									},
								},
								grid: {
									color: "rgba(196, 196, 196, 1)",
									lineWidth: 1,
								},
								border: {
									dash: [6, 6],
									color: "rgba(196, 196, 196, 1)",
								},
							},
							x: {
								title: {
									display: true,
									text: i18n.t("instructor.components.reports.month"),
									color: "#000000",
									font: {
										weight: 400,
										size: 15,
									},
								},
								grid: {
									lineWidth: 1,
								},
								border: {
									dash: [6, 6],
									color: "rgba(196, 196, 196, 1)",
								},
								ticks: {
									align: "start",
								},
							},
						},
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: {
								display: false,
								labels: {
									font: {
										size: 15, // Same font size as axes
										weight: 200,
									},
									color: "#2B2F32", // Same font color as axes
								},
							},
							tooltip: {
								displayColors: false,
								callbacks: {
									title: (context) => {
										return context[0].label.split(",").join(" ");
									},
								},
								titleFont: {
									weight: 600,
								},
							},
							centerText: {
								text: i18n.t("instructor.components.reports.canvas.noMethod"),
							},
						},
						elements: {
							point: {
								borderWidth: 0,
								radius: 0.5,
								backgroundColor: "#DBDBDB",
							},
						},
					},
				});
				setChart(newChart);
			}
		}
	}, [chartRef, chart, sessionData, loading]);

	useEffect(() => {
		if (!chart || monthLabels.length === 0 || loading) return;

		const visibleSessions = sessionData.filter((session) => session.show);
		const isOnlyOneVisible = visibleSessions.length === 1;
		
		if (!chart.options.plugins) {
			chart.options.plugins = {};
		}
		if (!chart.options.plugins.centerText) {
			chart.options.plugins.centerText = {};
		}
		if (monthLabels.length === 1) {
			chart.data.labels = [monthLabels[0], monthLabels[0]];
		} else {
			chart.data.labels = monthLabels;
		}

		chart.options.animation = { duration: 1000 };
		chart.options.plugins.centerText.display = visibleSessions.length === 0;
		chart.data.datasets = sessionData.map((session) => ({
			label: session.label,
			data: session.data?.length === 1
				? [session.data[0], session.data[0]]
				: (session.data ?? []),
			borderColor: session.color,
			backgroundColor: (session.show && isOnlyOneVisible)
				? `${session.color}1A`
				: "rgba(0, 0, 0, 0%)",
			pointBackgroundColor: session.color,
			hidden: !session.show,
			cubicInterpolationMode: "monotone",
			fill: true,
			pointHoverRadius: 5,
			pointHitRadius: 7,
			hoverBackgroundColor: session.color,
		}));
		chart.update();
	}, [chart, sessionData, monthLabels]);

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: {
					md: "1fr", // For small screens (1 column, 1x2 layout)
					lg: "28% 72%", // For medium and larger screens (2 columns, 2x1 layout)
				},
				gridTemplateRows: "auto",
				alignItems: "center",
				gap: "10px",
				marginX: { lg: "20px", md: "0px" },
				minWidth: "300px",
			}}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: "20px", marginRight: "24px" }}>
				{sessionData.map(({ label, data, show, setShow, color }, index) => {
					const labelId = `${index}-${canvasId}-checkbox-label`;
					const badgeId = `${index}-${canvasId}-badge-label`;
					const badgeCount = data?.reduce((acc, val) => acc + val, 0) ?? 0;

					return (
						<div key={label}>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									gap: "5px",
									justifyContent: "start",
									alignItems: "center",
								}}
							>
								<CoreCheckbox
									checked={show}
									onChange={() => setShow(!show)}
									sx={{ padding: "2px" }}
									size={"15px"}
									inputProps={{
										"aria-labelledby": `${labelId} ${badgeId}`,
									}}
								/>
								<Circle sx={{ color, width: "12px" }} />
								<div
									style={{
										fontSize: "15px",
										fontWeight: 500,
										textAlign: "left",
										color: "#2B2F32",
									}}
									id={labelId}
								>
									{label}
								</div>
								<div style={{ ...badgeStyle, backgroundColor: `${color}40` }} id={badgeId}>
									<span className={styles["sr-only"]}>
										{i18n.t("instructor.components.reports.canvas.numAssessment", {
											count: badgeCount,
										})}
									</span>
									<span aria-hidden={true}>{data ? badgeCount : <Skeleton width={"32px"} height={"16px"} />}</span>
								</div>
							</div>
							{index === 0 && (
								<hr
									style={{
										lineHeight: "0.5",
										width: "100%",
										backgroundColor: "#858687",
										margin: "0",
										marginTop: "20px",
									}}
								/>
							)}
						</div>
					);
				})}
			</div>
			<Box
				onMouseLeave={() => setHoveredIndex(null)}
				onKeyDown={handleKeyDown}
				role="region"
				tabIndex={0}
				aria-describedby={loading ? undefined : canvasId}
				sx={{
					height: "400px",
					position: "relative",
					minWidth: "300px",
					borderRadius: "4px",
					"&:focus-visible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: "2px",
					},
				}}
			>
				{loading ? (
					<Box display="flex" width="100%" height="100%" alignItems="center" justifyContent="center">
						<Skeleton variant="rounded" width={"90%"} height={"90%"} />
					</Box>
				) : (
					<>
						<canvas role="presentation" ref={chartRef} style={{ width: "100%" }} />
						<ScreenReaderTableResults monthLabels={monthLabels} sessionData={sessionData} id={canvasId} />
					</>
				)}
			</Box>
		</Box>
	);
}

function ReportTable({
	sessionData,
	monthLabels,
	loading
}: {
	sessionData: SessionData[];
	monthLabels: string[][];
	loading?: boolean;
}) {
	const tableRows: ReportTableRow[] = sessionData
		.filter((session) => session.show)
		.map((session) => {
			const row: ReportTableRow = {
				id: session.label,
				method: session.label,
			};

			monthLabels.forEach((_, index) => {
				row[`month_${index}`] = session.data?.[index] ?? 0;
			});

			return row;
		});

	const tableColumns: CoreDataViewColumn<ReportTableRow>[] = [
		{
			key: "method",
			header: i18n.t("instructor.components.reports.canvas.method"),
			cell: (item: ReportTableRow) => item.method,
			allowOverflow: true,
		},
		...monthLabels.map((labelParts, index) => ({
			key: `month_${index}` as keyof ReportTableRow,
			header: labelParts.join(" "),
			cell: (item: ReportTableRow) => item[`month_${index}`] ?? 0,
		})),
	];

	return (
		<CoreDataView
			data={tableRows}
			columns={tableColumns}
			loading={loading}
			className={styles.reportTable}
			strongHeaders
		/>
	);
}

export function ReportOverview({
	type,
	totalVeritySessions,
	totalLiveSessions,
	totalLiveCheckInSessions,
	totalNonLiveSessions,
	startDateFilter,
	endDateFilter,
}: {
	type: "course" | "institution";
	totalVeritySessions?: number[];
	totalLiveSessions?: number[];
	totalLiveCheckInSessions?: number[];
	totalNonLiveSessions?: number[];
	startDateFilter?: Date | null;
	endDateFilter?: Date | null;
}) {
	const [showVerity, setShowVerity] = useState(true);
	const [showLive, setShowLive] = useState(true);
	const [showLiveCheckIn, setShowLiveCheckIn] = useState(true);
	const [showNonLive, setShowNonLive] = useState(true);
	const [monthLabels, setMonthLabels] = useState<string[][]>([]);
	const [tab, setTab] = useState<"graph" | "table">("graph");
	const isLoading = totalVeritySessions === undefined;

	const tabList = [
		{ key: "graph", label: i18n.t("instructor.components.reports.canvas.tabs.graph") },
		{ key: "table", label: i18n.t("instructor.components.reports.canvas.tabs.table") }
	];

	const sessionData: SessionData[] = useMemo(() => [
		{
			label: i18n.t("instructor.components.reports.canvas.allMethodsLabel"),
			data: totalVeritySessions,
			show: showVerity,
			setShow: setShowVerity,
			color: "#4F75CB",
		},
		{
			label: i18n.t("instructor.components.reports.canvas.automatedLabel"),
			data: totalNonLiveSessions,
			show: showNonLive,
			setShow: setShowNonLive,
			color: "#E19278",
		},
		{
			label: i18n.t("instructor.components.reports.canvas.liveProctoringLabel"),
			data: totalLiveSessions,
			show: showLive,
			setShow: setShowLive,
			color: "#E992D8",
		},
		{
			label: i18n.t("instructor.components.reports.canvas.liveCheckInLabel"),
			data: totalLiveCheckInSessions,
			show: showLiveCheckIn,
			setShow: setShowLiveCheckIn,
			color: "#928FDC",
		},
	], [
		showVerity,
		showNonLive,
		showLive,
		showLiveCheckIn,
		totalVeritySessions,
		totalNonLiveSessions,
		totalLiveSessions,
		totalLiveCheckInSessions
	]);

	const getMonthLabels = (start: Date, end: Date) => {
		const labels: string[][] = [];
		const currentDate = new Date(start.getTime());
		while (currentDate <= end) {
			labels.push([`${currentDate.toLocaleString("default", { month: "short" })}`, `${currentDate.getFullYear()}`]);
			currentDate.setMonth(currentDate.getMonth() + 1);
		}
		return labels;
	};

	useEffect(() => {
		if (startDateFilter && endDateFilter) {
			setMonthLabels(getMonthLabels(startDateFilter, endDateFilter));
		}
	}, [endDateFilter, startDateFilter]);

	return (
		<CoreCard
			style={{ width: "100%", overflow: "auto" }}
			useHeader
			title={i18n.t("instructor.components.reports.canvas.title")}
			icon={SearchIcon}
		>
			<div style={{ display: "flex", width: "100%", flexDirection: "column", gap: "20px", minWidth: "300px" }}>
				<div
					style={{
						fontSize: "15px",
						fontWeight: 400,
						textAlign: "left",
						color: "#6B6B6B",
						marginTop: "10px",
					}}
				>
					{i18n.t("instructor.components.reports.canvas.description", { type: type })}
				</div>
				<div className={styles.tabsContainer}>
					<CoreTabs
						tabs={tabList}
						value={tab}
						onChange={(_, newTab) => setTab(newTab)}
						secondary
						height="45px"
						tabsProps={{ sx: { height: "fit-content" } }}
					/>
				</div>
				{tab === "graph" && (
					<ReportGraph sessionData={sessionData} monthLabels={monthLabels} loading={isLoading} />
				)}
				{tab === "table" && (
					<ReportTable sessionData={sessionData} monthLabels={monthLabels} loading={isLoading} />
				)}
			</div>
		</CoreCard>
	);
}

function CustomDoughnutChart({
	title,
	labels,
	data,
	backgroundColors,
	centerText,
	secondaryText,
	labelText,
	semiCircle,
	smallText,
}: {
	title: string;
	labels: string[];
	data: number[];
	backgroundColors: string[];
	centerText: string;
	secondaryText?: string;
	labelText?: string;
	semiCircle?: boolean;
	smallText?: boolean;
}) {
	const chartRef = useRef<HTMLCanvasElement>(null);
	const chartInstanceRef = useRef<Chart<"doughnut", number[], string> | null>(null);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

	// Filter out items with data value of 0
	const filteredData = data.filter((value) => value !== 0);
	const filteredLabels = labels.filter((_, index) => data[index] !== 0);
	const filteredBackgroundColors = backgroundColors.filter((_, index) => data[index] !== 0);

	const handleHover = (_event: ChartEvent, elements: ActiveElement[]) => {
		if (elements.length > 0) {
			const index = elements[0].index;
			setHoveredIndex(index);
		} else {
			setHoveredIndex(null);
		}
	};

	useEffect(() => {
		if (chartRef.current) {
			const ctx = chartRef.current.getContext("2d");

			if (ctx) {
				const sum = filteredData.reduce((sum, v) => sum + v, 0);

				// Destroy the previous chart instance if it exists
				if (chartInstanceRef.current) {
					chartInstanceRef.current.destroy();
				}

				// Create the chart instance
				chartInstanceRef.current = new Chart(ctx, {
					type: "doughnut",
					data: {
						labels: filteredLabels,
						datasets: [
							{
								label: title,
								data: filteredData,
								backgroundColor: filteredBackgroundColors,
							},
						],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						cutout: "70%",
						onHover: handleHover,
						...(semiCircle && { rotation: -90, circumference: 180 }),
						plugins: {
							legend: {
								display: false, // Disable default legend
							},
							centerText: {
								display: true,
								text: `${centerText}\n${secondaryText ? secondaryText : sum}`,
								semiCircle: semiCircle,
								smallText: smallText,
							},
							tooltip: {
								displayColors: false,
								titleFont: {
									weight: 600,
								},
								bodyFont: {
									weight: 200,
								},
							},
						},
					},
				});
			}
		}
	}, []);

	return (
		<Box
			minHeight={semiCircle ? "180px" : "210px"}
			paddingY={semiCircle ? "0px" : "40px"}
			sx={{
				display: "grid",
				gridTemplateColumns: labelText
					? {
							md: "repeat(1, 1fr)",
							lg: "1fr 1fr",
						}
					: "5fr 3fr",
				gridTemplateRows: "auto",
				columnGap: "min(120px, 20%)",
				rowGap: "2px",
				alignItems: "center",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: labelText
						? {
								md: "flex-start",
								lg: "flex-end",
							}
						: "flex-end",
				}}
			>
				<div
					style={{
						position: "relative",
						width: "100%",
						maxWidth: "210px",
						minWidth: "210px",
						maxHeight: semiCircle ? "140px" : "210px",
						minHeight: semiCircle ? "140px" : "210px"
					}}
				>
					<canvas
						ref={chartRef}
						style={{ width: "100%", height: "100%"  }}
						onMouseLeave={() => setHoveredIndex(null)}
					/>
				</div>
			</Box>

			{/* Custom Legend */}
			<Box
				display="flex"
				flexDirection="column"
				gap="10px"
				paddingRight="8px"
				onMouseEnter={() => setHoveredIndex(null)}
			>
				{filteredLabels.map((label, index) => (
					<Box key={index} display="flex" alignItems="center" gap="10px">
						<Circle
							sx={{
								width: "15px",
								height: "15px",
								color: filteredBackgroundColors[index],
							}}
						/>
						<Typography fontWeight={hoveredIndex === index ? 700 : 500} fontSize="14px" lineHeight={1.6}>
							{label +
								(labelText ? ` - ${filteredData[index]} ${labelText + (filteredData[index] > 1 ? "s" : "")}` : "")}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
}

export function SeverityChart({
	severityCounts,
}: {
	severityCounts?: { none: number; minor: number; major: number; severe: number };
}) {
	const labels = [
		i18n.t("instructor.components.reports.severe"),
		i18n.t("instructor.components.reports.major"),
		i18n.t("instructor.components.reports.minor"),
		i18n.t("instructor.components.reports.noFlags"),
	];
	const backgroundColors = ["#E2726E", "#EBBE79", "#6DA186", "#E6E6E6"];
	const data = [
		severityCounts?.severe ?? 0,
		severityCounts?.major ?? 0,
		severityCounts?.minor ?? 0,
		severityCounts?.none ?? 0,
	];

	return (
		<CoreCard
			icon={Flag}
			iconStroke={true}
			useHeader
			title={i18n.t("instructor.components.reports.severityChart.title")}
			style={{ overflow: "auto", minHeight: "350px" }}
		>
			{severityCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
								marginTop: "10px",
								marginBottom: "20px",
							}}
						>
							{i18n.t("instructor.components.reports.severityChart.viewHighestFlag")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("instructor.components.reports.severityChart.severityLevels")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Total"
						/>
					</>
				) : (
					<NoResults text={i18n.t("instructor.components.reports.severityChart.noSeverityData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}

export function AssessmentCompletionChart({
	completionCounts,
}: {
	completionCounts?: { complete: number; incomplete: number; unavailable: number; pending: number; error: number };
}) {
	const labels = [
		i18n.t("instructor.components.reports.completionChart.complete"),
		i18n.t("instructor.components.reports.completionChart.incomplete"),
		i18n.t("instructor.components.reports.completionChart.unavailable"),
		i18n.t("instructor.components.reports.completionChart.pending"),
		i18n.t("instructor.components.reports.completionChart.error"),
	];
	const backgroundColors = ["#81CCC9", "#E992D8", "#E19278", "#928FDC", "#939393"];
	const data = [
		completionCounts?.complete ?? 0,
		completionCounts?.incomplete ?? 0,
		completionCounts?.unavailable ?? 0,
		completionCounts?.pending ?? 0,
		completionCounts?.error ?? 0,
	];

	return (
		<CoreCard
			icon={AssessmentOverviewIcon}
			iconStroke={true}
			useHeader
			title={i18n.t("instructor.components.reports.completionChart.title")}
			style={{ overflow: "auto", minHeight: "200px", width: "100%" }}
		>
			{completionCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
							}}
						>
							{i18n.t("instructor.components.reports.completionChart.viewProgress")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("instructor.components.reports.completionChart.completionLevels")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Progress"
							secondaryText="Overview"
							labelText={i18n.t("instructor.components.reports.testTaker")}
							semiCircle
							smallText
						/>
					</>
				) : (
					<NoResults text={i18n.t("instructor.components.reports.completionChart.noCompletionData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}

export function AssessmentSeverityChart({
	severityCounts,
}: {
	severityCounts?: { none: number; minor: number; major: number; severe: number };
}) {
	const labels = [
		i18n.t("instructor.components.reports.severe"),
		i18n.t("instructor.components.reports.major"),
		i18n.t("instructor.components.reports.minor"),
		i18n.t("instructor.components.reports.noFlags"),
	];
	const backgroundColors = ["#E2726E", "#EBBE79", "#6DA186", "#E6E6E6"];
	const data = [
		severityCounts?.severe ?? 0,
		severityCounts?.major ?? 0,
		severityCounts?.minor ?? 0,
		severityCounts?.none ?? 0,
	];

	return (
		<CoreCard
			icon={Flag}
			iconStroke={true}
			useHeader
			title={i18n.t("instructor.components.reports.title")}
			style={{ overflow: "auto", minHeight: "200px", width: "100%" }}
		>
			{severityCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
							}}
						>
							{i18n.t("instructor.components.reports.description")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("instructor.components.reports.severityLevel")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Flag"
							secondaryText="Overview"
							labelText={i18n.t("instructor.components.reports.testTaker")}
							semiCircle
							smallText
						/>
					</>
				) : (
					<NoResults text={i18n.t("instructor.components.reports.noSeverityData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}

export function PlatformChart({
	platformCounts,
}: {
	platformCounts?: { lockdownCount: number; mobileCount: number; extensionCount: number };
}) {
	const labels = [
		i18n.t("portal.components.assessmentPageTSX.columnList.lockdown"),
		i18n.t("portal.components.assessmentPageTSX.columnList.mobileAndLockdown"),
		i18n.t("portal.components.assessmentPageTSX.columnList.extension"),
	];
	const backgroundColors = ["rgba(225, 146, 120, 1)", "rgba(235, 190, 121, 1)", "rgba(233, 146, 216, 1)"];
	const data = [
		platformCounts?.lockdownCount ?? 0,
		platformCounts?.mobileCount ?? 0,
		platformCounts?.extensionCount ?? 0,
	];

	return (
		<CoreCard
			icon={PlatformIcon}
			useHeader
			title={i18n.t("portal.components.assessmentPageTSX.columnList.usage")}
			style={{ overflow: "auto", minHeight: "350px" }}
		>
			{platformCounts ? (
				data.reduce((sum, value) => sum + value, 0) > 0 ? (
					<>
						<div
							style={{
								fontSize: "15px",
								fontWeight: 400,
								textAlign: "left",
								color: "#6B6B6B",
								marginTop: "10px",
								marginBottom: "20px",
							}}
						>
							{i18n.t("portal.components.assessmentPageTSX.columnList.description")}
						</div>
						<CustomDoughnutChart
							title={i18n.t("portal.components.assessmentPageTSX.columnList.types")}
							labels={labels}
							data={data}
							backgroundColors={backgroundColors}
							centerText="Total"
						/>
					</>
				) : (
					<NoResults text={i18n.t("portal.components.assessmentPageTSX.columnList.noPlatformData")} />
				)
			) : (
				<Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
					<Skeleton variant="rounded" width="90%" height="80%" />
				</Box>
			)}
		</CoreCard>
	);
}